import { AQLogger } from "aq-logger";
import {
    DeliveryApi,
    NotificationsApi,
    OrdersApi,
    ShippingAddressesApi,
    type DTODeliverySlotSelectionDTO,
    type LanguageCode,
    type ShippingAddressesRequestCreateShippingAddressRequest,
    type ShippingAddressesRequestUpdateShippingAddressRequest
} from "../../../generated/api-client";
import type { LocaleCode } from "@/i18n/locales";
import { generatedConfiguration } from "@/services/api/generated-client";
import { GENERAL_USER_ERROR_MESSAGE } from "@/services/api/service-errors";

const logger = new AQLogger("StorefrontApi");
const addresses = new ShippingAddressesApi(generatedConfiguration);
const orders = new OrdersApi(generatedConfiguration);
const notifications = new NotificationsApi(generatedConfiguration);
const delivery = new DeliveryApi(generatedConfiguration);

export const storefrontApi = {
    async listAddresses() {
        try {
            logger.debug("Listing shipping addresses...");
            const response = await addresses.shippingAddressesList();
            if (response.reject || !response.data) {
                throw logger.exception("Failed to list shipping addresses.", "shipping addresses list rejected");
            }
            logger.success("Shipping addresses listed.");
            return response.data.addresses;
        } catch (error) {
            throw logger.exception("Failed to list shipping addresses.", error);
        }
    },
    async createAddress(payload: ShippingAddressesRequestCreateShippingAddressRequest) {
        try {
            logger.debug("Creating shipping address...");
            const response = await addresses.shippingAddressesCreate({ shippingAddressesRequestCreateShippingAddressRequest: payload });
            if (response.reject || !response.data) {
                throw logger.exception("Failed to create shipping address.", "shipping address create rejected");
            }
            logger.success("Shipping address created.");
            return response.data.address;
        } catch (error) {
            throw logger.exception("Failed to create shipping address.", error);
        }
    },
    async updateAddress(id: string, payload: ShippingAddressesRequestUpdateShippingAddressRequest) {
        try {
            logger.debug("Updating shipping address...");
            const response = await addresses.shippingAddressesUpdate({ shippingAddressId: id, shippingAddressesRequestUpdateShippingAddressRequest: payload });
            if (response.reject || !response.data) {
                throw logger.exception("Failed to update shipping address.", "shipping address update rejected");
            }
            logger.success("Shipping address updated.");
            return response.data.address;
        } catch (error) {
            throw logger.exception("Failed to update shipping address.", error);
        }
    },
    async deleteAddress(id: string): Promise<void> {
        try {
            logger.debug("Deleting shipping address...");
            const response = await addresses.shippingAddressesDelete({ shippingAddressId: id });
            if (response.reject) {
                throw logger.exception("Failed to delete shipping address.", "shipping address delete rejected");
            }
            logger.success("Shipping address deleted.");
            return;
        } catch (error) {
            throw logger.exception("Failed to delete shipping address.", error);
        }
    },
    async setDefaultAddress(id: string): Promise<void> {
        try {
            logger.debug("Setting default shipping address...");
            const response = await addresses.shippingAddressesSetDefault({ shippingAddressId: id });
            if (response.reject) {
                throw logger.exception("Failed to set default shipping address.", "default shipping address update rejected");
            }
            logger.success("Default shipping address updated.");
            return;
        } catch (error) {
            throw logger.exception("Failed to set default shipping address.", error);
        }
    },
    async listOrders(locale: LocaleCode, orderNumber?: string) {
        try {
            logger.debug("Listing orders...");
            const response = await orders.ordersGetOrders({ languageCode: locale as LanguageCode, orderNumber });
            if (response.reject || !response.data) {
                throw logger.exception("Failed to list orders.", "orders list rejected");
            }
            logger.success("Orders listed.");
            return response.data.orders;
        } catch (error) {
            throw logger.exception("Failed to list orders.", error);
        }
    },
    async getOrderItems(locale: LocaleCode, orderNumber: string) {
        try {
            logger.debug("Fetching order items...");
            const response = await orders.ordersGetOrderItems({ languageCode: locale as LanguageCode, orderNumber });
            if (response.reject || !response.data) {
                throw logger.exception("Failed to get order items.", "order items fetch rejected");
            }
            logger.success("Order items fetched.");
            return response.data.orderItems;
        } catch (error) {
            throw logger.exception("Failed to get order items.", error);
        }
    },
    async getDeliveryProgress(orderNumber: string) {
        try {
            const response = await orders.ordersGetDeliveryProgress({ orderNumber });
            if (response.reject || !response.data) {
                throw logger.exception("Failed to get delivery progress.", "delivery progress rejected");
            }
            return response.data.progress;
        } catch (error) {
            throw logger.exception("Failed to get delivery progress.", error);
        }
    },
    async finalizeOrder(shippingAddressId: string, deliverySlot?: DTODeliverySlotSelectionDTO | null) {
        try {
            logger.debug("Finalizing order...");
            const response = await orders.ordersFinalizeOrder({ ordersRequestFinalizeOrderRequest: { shippingAddressId, deliverySlot: deliverySlot ?? undefined } });
            if (response.reject || !response.data) {
                throw logger.exception("Failed to finalize order.", "order finalize rejected");
            }
            logger.success("Order finalized.");
            return response.data;
        } catch (error) {
            throw logger.exception("Failed to finalize order.", error);
        }
    },
    async getDeliveryAvailability(latitude: number, longitude: number, fromDate?: string, toDate?: string) {
        try {
            logger.debug("Fetching delivery availability...");
            const response = await delivery.deliveryAvailabilityFetchAvailability({
                deliveryAvailabilityPayloadFetchAvailabilityRequestPayload: { latitude, longitude, fromDate, toDate },
            });
            if (response.reject || !response.data) {
                throw logger.exception("Failed to fetch delivery availability.", "delivery availability rejected");
            }
            logger.success("Delivery availability fetched.");
            return response.data;
        } catch (error) {
            throw logger.exception("Failed to fetch delivery availability.", error);
        }
    },
    async reserveDeliverySlot(shippingAddressId: string, deliverySlot: DTODeliverySlotSelectionDTO) {
        try {
            const response = await delivery.deliveryAvailabilityReserveSlot({
                deliveryAvailabilityPayloadReserveSlotRequestPayload: { shippingAddressId, deliverySlot },
            });
            if (response.reject || !response.data) {
                throw logger.exception("Failed to reserve delivery slot.", "delivery slot reservation rejected");
            }
            return response.data.reservation;
        } catch (error) {
            throw logger.exception("Failed to reserve delivery slot.", error);
        }
    },
    async listNotifications() {
        try {
            logger.debug("Listing notifications...");
            const response = await notifications.notificationsGetNotifications();
            if (response.reject || !response.data) {
                throw logger.exception("Failed to list notifications.", "notifications list rejected");
            }
            logger.success("Notifications listed.");
            return response.data.notifications;
        } catch (error) {
            throw logger.exception("Failed to list notifications.", error);
        }
    },
    async markNotificationRead(id: string): Promise<void> {
        try {
            logger.debug("Marking notification as read...");
            const response = await notifications.notificationsMarkNotificationRead({ notificationId: id });
            if (response.reject) {
                throw logger.exception("Failed to update notification.", "notification update rejected");
            }
            logger.success("Notification marked as read.");
            return;
        } catch (error) {
            throw logger.exception("Failed to update notification.", error);
        }
    },
    async archiveNotification(id: string, archived: boolean): Promise<void> {
        try {
            logger.debug(`Archiving notification...`);
            const response = archived
                ? await notifications.notificationsArchiveNotification({ notificationId: id })
                : await notifications.notificationsRestoreNotification({ notificationId: id });
            if (response.reject) {
                throw logger.exception("Failed to update notification.", archived ? "notification archive rejected" : "notification restore rejected");
            }
            logger.success(archived ? "Notification archived." : "Notification restored.");
            return;
        } catch (error) {
            throw logger.exception("Failed to update notification.", error);
        }
    }
};
